import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, Icon, Modal, Text} from '@forecast-it/design-system';
import Util from '../../../forecast-app/shared/util/util';
import SetFeatureFlagGlobal from '../../../mutations/mission_control/set_feature_flag_global_mutation';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {FeatureFlagDetailData} from './featureFlagDetailData';
import {FEATURE_FLAG_DESTRUCTIVE_TEXT, featureFlagCardSurface} from './featureFlagSurface';

const Card = styled.div`
	${featureFlagCardSurface}
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px 20px;
	margin-top: 12px;
	padding: 16px 20px;
`;

const Prose = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const Body = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

// See FeatureFlagRemoveAllModal's identical wrapper: the design system's Icon cannot inherit a CSS
// colour, so the destructive tone has to be passed as its own `newColor` prop; this span only holds
// the glyph's size steady while the prose beside it wraps.
const WarningGlyph = styled.span`
	display: flex;
	flex: none;
	margin-top: 2px;
`;

const ModalProse = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
`;

/* The typography component paints its own colour onto the element it renders, so the warning's red has
   to be declared on this wrapper's child rather than passed as a colour prop. */
const Irreversible = styled.span`
	> * {
		color: ${FEATURE_FLAG_DESTRUCTIVE_TEXT};
	}
`;

const UNTRACKED_GRANT_NOTE =
	'This flag was enabled for every company outside rollout tracking. Roll it out in code, then disable this grant here once that rollout has reached production. Disabling it before then removes the feature from every company that has no grant of its own.';

const ROLLED_OUT_GRANT_NOTE =
	'This flag is rolled out in code, so this grant has no effect while that rollout stands. Disable it now: if the rollout is ever reverted, this grant would keep the feature on for every company.';

export interface FeatureFlagGlobalToggleProps {
	detail: FeatureFlagDetailData;
}

/**
 * The platform-wide on/off control for a flag: a button behind a confirm dialog that names the flag
 * key, and — once enabled — a line saying when and by whom.
 *
 * Only a restrictive (kill-switch) flag that is not rolled out can be turned on platform-wide. A
 * non-restrictive flag that already carries a platform-wide grant gets the status line, a Disable button
 * and a note that the grant sits outside rollout tracking; a non-restrictive flag without one renders
 * nothing.
 *
 * A rolled-out flag reaches the detail screen only while it still carries a platform-wide grant (see
 * `findDetailFeature`), so here it only ever gets Disable. Its note says why the grant should go: it does
 * nothing while the rollout stands, and would keep the feature on for everyone if the rollout were
 * reverted.
 *
 * `enabledForAllCompaniesBy` is a bare person id: the flag list the query already carries (forecast and
 * other persons) does not necessarily include the operator who made the platform-wide grant, since
 * platform-wide changes are not scoped to any one company's user lists. Rather than resolve a name from
 * a population that might not contain them, the id is shown as-is — the same choice the backend's own
 * runbook makes when reading the events table directly.
 */
export function FeatureFlagGlobalToggle({detail}: FeatureFlagGlobalToggleProps): JSX.Element | null {
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const enabling = !detail.enabledForAllCompanies;
	const verb = enabling ? 'Enable' : 'Disable';

	const commitToggle = (): void => {
		if (isSubmitting || (enabling && (!detail.restrictive || detail.rolledOut))) {
			return;
		}
		setIsSubmitting(true);
		Util.CommitMutation(
			SetFeatureFlagGlobal,
			{featureFlag: detail.key, enabled: enabling},
			() => {
				setIsSubmitting(false);
				setIsConfirmOpen(false);
				createToast({
					duration: 5000,
					message: `${detail.key} ${enabling ? 'enabled' : 'disabled'} for all companies`,
				});
			},
			true,
			null,
			(transaction, onFailure) => {
				// See FeatureFlagDetail's commitRemoval for why a retrying transient failure must not touch
				// this state, and why a genuine failure has to close the dialog rather than leave it stuck.
				const retrying = onFailure(transaction);
				if (retrying) {
					return;
				}
				setIsSubmitting(false);
				setIsConfirmOpen(false);
			}
		);
	};

	if (!detail.restrictive && !detail.enabledForAllCompanies) {
		return null;
	}

	return (
		<Card>
			<Prose>
				<Detail>Platform-wide</Detail>
				<div data-cy="feature-flag-global-status">
					{detail.enabledForAllCompanies ? (
						<Text>
							{`Enabled for all companies since ${detail.enabledForAllCompaniesDateLabel || 'an unknown date'}` +
								(detail.enabledForAllCompaniesBy != null
									? ` by person ${detail.enabledForAllCompaniesBy}`
									: '')}
						</Text>
					) : (
						<Text color="medium">Not enabled for all companies.</Text>
					)}
				</div>
				{!detail.restrictive || detail.rolledOut ? (
					<div data-cy="feature-flag-global-untracked-note">
						<Detail color="medium">{detail.rolledOut ? ROLLED_OUT_GRANT_NOTE : UNTRACKED_GRANT_NOTE}</Detail>
					</div>
				) : null}
			</Prose>
			<Button
				data-cy={`feature-flag-global-toggle-${detail.key}`}
				emphasis={EMPHASIS.MEDIUM}
				onPress={() => setIsConfirmOpen(true)}
			>
				{`${verb} for all companies`}
			</Button>
			{isConfirmOpen ? (
				<Modal closeOnInteractOutside={false} onCloseModal={() => setIsConfirmOpen(false)} size={Modal.SIZE.SMALL}>
					<Modal.Title>{`${verb} "${detail.key}" for all companies?`}</Modal.Title>
					<Modal.Content>
						<Body>
							<WarningGlyph>
								{/* Decorative: the heading already says this is a platform-wide change, so an
								    accessible name here would only repeat it. */}
								<Icon aria-hidden="true" icon="warning" newColor="error" size="m" />
							</WarningGlyph>
							<ModalProse>
								<Detail color="medium">
									{enabling
										? `Every company gains "${detail.key}" immediately, with no rollout and no eligible-pool check.`
										: detail.rolledOut
										? `Every company keeps "${detail.key}" through its rollout in code. Only this platform-wide grant is removed.`
										: `Every company without its own grant of "${detail.key}" loses it immediately. Companies and users granted it individually or through a rollout keep it.`}
								</Detail>
								<Irreversible>
									<Text>This takes effect immediately and is not scoped to a cohort or a percentage.</Text>
								</Irreversible>
							</ModalProse>
						</Body>
					</Modal.Content>
					<Modal.Footer>
						<Modal.TertiaryFooterButton
							dataCy="feature-flag-global-toggle-cancel"
							disabled={isSubmitting}
							onPress={() => setIsConfirmOpen(false)}
						>
							Cancel
						</Modal.TertiaryFooterButton>
						<Modal.PrimaryFooterButton
							dataCy="feature-flag-global-toggle-confirm"
							disabled={isSubmitting}
							onPress={commitToggle}
						>
							{`${verb} for all companies`}
						</Modal.PrimaryFooterButton>
					</Modal.Footer>
				</Modal>
			) : null}
		</Card>
	);
}
